import React, { useState, useEffect } from 'react';
import { Alert, Card, Tag, List, Spin, Empty } from 'antd';
import { BellOutlined, ClockCircleOutlined, WarningOutlined } from '@ant-design/icons';
import apiClient from '../api/apiClient';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { getCicloAcademico } from '../utils/cicloEscolar';

dayjs.extend(customParseFormat);

const Home = ({ usuario, onLogout }) => {
  const [notificaciones, setNotificaciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [solvenciaFamilia, setSolvenciaFamilia] = useState(null);

  useEffect(() => {
    if (usuario?.rol === 4 && usuario?.IdDocente) {
      cargarNotificaciones();
    }
    if (usuario?.rol === 3) {
      cargarSolvenciaFamilia();
    }
  }, [usuario]); // eslint-disable-line react-hooks/exhaustive-deps

  const cargarNotificaciones = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/notificaciones-docentes/pendientes/${usuario.IdDocente}`);
      if (response.data.success) {
        setNotificaciones(response.data.notificaciones || []);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const cargarSolvenciaFamilia = async () => {
    try {
      const perfilRes = await apiClient.get('/login/perfil');
      const IdFamilia = perfilRes.data.data?.IdFamilia;
      if (!IdFamilia) return;
      // Ciclo académico (cambia en enero): en nov/dic se evalúa el ciclo que termina, no el de inscripción
      const ciclo = getCicloAcademico();

      // Cargar hijos inscritos y solvencia en paralelo
      const [hijosRes, solvenciaRes] = await Promise.allSettled([
        apiClient.get(`/familias/hijosporfamilia/${IdFamilia}`, { params: { cicloEscolar: ciclo } }),
        apiClient.get(`/pagos/solvencia-familia/${IdFamilia}`, { params: { cicloEscolar: ciclo } }),
      ]);

      if (solvenciaRes.status === 'fulfilled' && solvenciaRes.value.data.success) {
        const raw = solvenciaRes.value.data.data;

        // IDs de hijos con inscripción activa en el ciclo actual
        const idsInscritos = new Set(
          hijosRes.status === 'fulfilled' && hijosRes.value.data.success
            ? (hijosRes.value.data.data || []).map(h => h.IdAlumno)
            : []
        );

        // Filtrar la respuesta de solvencia para ignorar hijos sin inscripción activa
        if (Array.isArray(raw)) {
          setSolvenciaFamilia(raw.filter(h => idsInscritos.has(h.IdAlumno)));
        } else if (raw?.hijos) {
          setSolvenciaFamilia({ ...raw, hijos: raw.hijos.filter(h => idsInscritos.has(h.IdAlumno)) });
        } else {
          setSolvenciaFamilia(raw);
        }
      }
    } catch (error) {
      // Silencioso: no bloquear el home si falla
    }
  };

  const calcularDiasRestantes = (fechaLimite) => {
    const hoy = new Date();
    const limite = new Date(fechaLimite);
    const diferencia = Math.ceil((limite - hoy) / (1000 * 60 * 60 * 24));
    return diferencia;
  };

  const getTipoColor = (tipo) => {
    if (tipo === 'ACTIVIDADES_INCOMPLETAS') return 'orange';
    if (tipo === 'CALIFICACIONES_PENDIENTES') return 'red';
    return 'blue';
  };

  const hijosInsolventes = (() => {
    if (!solvenciaFamilia) return [];
    const lista = Array.isArray(solvenciaFamilia) ? solvenciaFamilia : (solvenciaFamilia.hijos || []);
    return lista.filter(h => h.PuedeVerNotas === false);
  })();
  const familiaInsolvente = solvenciaFamilia && !Array.isArray(solvenciaFamilia) && solvenciaFamilia.PuedeVerNotas === false;

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <h2>Bienvenido, {usuario?.nombre || 'Usuario'}</h2>
      <p>Tu sesión está activa.</p>

      {usuario?.rol === 3 && solvenciaFamilia !== null && (familiaInsolvente || hijosInsolventes.length > 0) && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Pagos pendientes"
          description={
            familiaInsolvente
              ? 'Tu familia no está al día en los pagos. No podrás consultar las boletas de calificaciones hasta regularizar el estado de cuenta.'
              : `${hijosInsolventes.length === 1 ? 'Un hijo no está' : `${hijosInsolventes.length} hijos no están`} al día en los pagos. No podrás consultar las boletas de calificaciones de ${hijosInsolventes.length === 1 ? 'ese estudiante' : 'esos estudiantes'} hasta regularizar el estado de cuenta.`
          }
        />
      )}

      {/* Mostrar notificaciones solo para docentes */}
      {usuario?.rol === 4 && (
        <div style={{ marginTop: '30px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" />
            </div>
          ) : notificaciones.length > 0 ? (
            <>
              <Alert
                message={`Tienes ${notificaciones.length} notificación(es) pendiente(s)`}
                description="Por favor completa las siguientes tareas antes de la fecha límite"
                type="warning"
                icon={<WarningOutlined />}
                showIcon
                style={{ marginBottom: '20px' }}
              />

              <List
                grid={{ gutter: 16, xs: 1, sm: 1, md: 2, lg: 2, xl: 2 }}
                dataSource={notificaciones}
                renderItem={(notif) => {
                  const diasRestantes = calcularDiasRestantes(notif.FechaLimite);
                  const esUrgente = diasRestantes <= 1;

                  return (
                    <List.Item>
                      <Card
                        size="small"
                        title={
                          <span>
                            <BellOutlined style={{ marginRight: 8 }} />
                            {notif.NombreCurso} - {notif.NombreGrado} {notif.NombreSeccion}
                          </span>
                        }
                        extra={
                          <Tag color={getTipoColor(notif.TipoNotificacion)}>
                            {notif.TipoNotificacion.replace('_', ' ')}
                          </Tag>
                        }
                        style={{
                          borderLeft: esUrgente ? '4px solid #ff4d4f' : '4px solid #faad14'
                        }}
                      >
                        <p style={{ marginBottom: '8px' }}>{notif.Mensaje}</p>
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
                          <div><strong>Unidad:</strong> {notif.NombreUnidad}</div>
                          <div><strong>Jornada:</strong> {notif.NombreJornada}</div>
                        </div>
                        <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '12px', color: '#999' }}>
                            <ClockCircleOutlined /> Fecha límite: {dayjs(notif.FechaLimite, 'YYYY-MM-DD').format('DD/MM/YYYY')}
                          </span>
                          {esUrgente ? (
                            <Tag color="red">¡URGENTE! {diasRestantes} día(s)</Tag>
                          ) : (
                            <Tag color="orange">{diasRestantes} día(s) restantes</Tag>
                          )}
                        </div>
                      </Card>
                    </List.Item>
                  );
                }}
              />
            </>
          ) : (
            <Empty description="No tienes notificaciones pendientes" />
          )}
        </div>
      )}
    </div>
  );
};

export default Home;